import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export const SUBSECTION_PARAM_NAME = "subsection";
export const ORDER_PARAM_NAME = "order";
export const ORDERSECTION_PARAM_NAME = "ordersection";

/**
 * Reads a tab-like selection from a search param, keeps it in the URL, and sanitises it.
 *
 * The valid values can be a fixed enum (sub-tabs) or a dynamic id list (orders).
 * When the param is missing or invalid, the hook redirects to the default with
 * `{ replace: true }`. When the valid list is empty, the hook writes nothing and
 * returns the default.
 */
export function useSearchParamTab<T extends string>(
	paramName: string,
	validValues: readonly T[],
	defaultValue: T,
): [T, (value: T) => void] {
	const [searchParams, setSearchParams] = useSearchParams();

	const rawValue = searchParams.get(paramName);
	const isValid = null !== rawValue && (validValues as readonly string[]).includes(rawValue);
	const currentValue = isValid ? (rawValue as T) : defaultValue;

	useEffect(() => {
		if (0 === validValues.length) return;
		if (isValid) return;
		setSearchParams((prev) => {
			prev.set(paramName, defaultValue);
			return prev;
		}, { replace: true });
	}, [isValid, paramName, defaultValue, validValues.length, setSearchParams]);

	const setValue = useCallback((value: T) => {
		setSearchParams((prev) => {
			prev.set(paramName, value);
			return prev;
		});
	}, [paramName, setSearchParams]);

	return [currentValue, setValue];
}
